import { extractApiError } from 'utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  changePasswordRequest,
  createNotificationRequest,
  decideProviderPhotoRequest,
  decideProviderVerificationRequest,
  editUserRequest,
  getDoctorFinancesRequest,
  getDoctorsRequest,
  getDoctorVisitsRequest,
  getPendingPhotosCountRequest,
  getPendingPhotosRequest,
  getPendingProvidersCountRequest,
  getPendingProvidersRequest,
  getMeRequest,
  getNotificationRequest,
  getPatientVisitsRequest,
  getUserRequest,
  getUsersRequest,
  logInRequest,
  LoginRequestType,
  restorePasswordRequest,
  signUpRequest,
  SignUpRequestType,
  verifyResetRequest,
} from 'api';
import { LsKeys, routes } from 'enums';
import { authActions } from 'state';

export const getDoctors = createAsyncThunk(
  'doctors/get',
  async (query: any, { rejectWithValue, getState, dispatch }) => {
    try {
      const res = await getDoctorsRequest(query);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    } finally {
      console.log('finally');
    }
  },
);

export const getDoctor = createAsyncThunk(
  'user/get',
  async (id: any, { rejectWithValue, getState, dispatch }) => {
    try {
      const res = await getUserRequest(id);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    } finally {
      console.log('finally');
    }
  },
);

export const editUser = createAsyncThunk(
  'user/patch',
  async ({ data, id }: any, { rejectWithValue, getState, dispatch }) => {
    try {
      const res = await editUserRequest(id, data);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    } finally {
      console.log('finally');
    }
  },
);

/**
 * A provider's visit history.
 *
 * The type prefix is `doctorVisits/get`, and it must stay distinct from
 * `getPatientVisits`, which was also declared `visits/get`. Redux Toolkit
 * derives the action type from that string, so the two thunks dispatched
 * IDENTICAL actions and each one's response was written into both slices. The
 * provider page rendered `doctorVisits` while paging on `state.users.visits`,
 * and that appeared to work only because the collision kept the two in step.
 *
 * Note this is the opposite call from `getUser` / `getDoctor`, which share
 * `user/get` deliberately so one refresh updates both detail pages. That
 * sharing is between two reads of the SAME row; this one was between two
 * different people's visit lists.
 */
export const getDoctorVisits = createAsyncThunk(
  'doctorVisits/get',
  async (query: any, { rejectWithValue, getState, dispatch }) => {
    try {
      const res = await getDoctorVisitsRequest(query);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    } finally {
      console.log('finally');
    }
  },
);

export const getDoctorFinances = createAsyncThunk(
  'finances/get',
  async (query: any, { rejectWithValue, getState, dispatch }) => {
    try {
      const res = await getDoctorFinancesRequest(query);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    } finally {
      console.log('finally');
    }
  },
);

// --- Provider approval -------------------------------------------------------

export const getPendingProviders = createAsyncThunk(
  'providers/pending/get',
  async (query: any, { rejectWithValue }) => {
    try {
      const res = await getPendingProvidersRequest(query);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

export const getPendingProvidersCount = createAsyncThunk(
  'providers/pending/count',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getPendingProvidersCountRequest();

      return res.data?.count ?? 0;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

/**
 * What an operator is told when a provider decision is refused.
 *
 * Same pattern as `CLOSE_ACCOUNT_ERRORS` and `STATUS_CHANGE_ERRORS` in
 * `userThunk`: keyed on the API's `code`, which is a contract, rather than on
 * its prose, which is not.
 *
 * The two that matter are races. Two administrators working the same approval
 * queue is the normal way this console is used, and the loser needs to be told
 * the decision was already made rather than left looking at a card that did
 * nothing when clicked.
 */
export const DECISION_ERRORS: Record<string, string> = {
  PROVIDER_ALREADY_REVIEWED:
    'This provider has already been reviewed, probably by another ' +
    'administrator. Refresh to see the decision that was recorded.',
  NO_PHOTO_AWAITING_REVIEW:
    'There is no photo awaiting a decision on this provider. Refresh the page.',
  NOT_A_PROVIDER: 'This account is not a provider, so it has nothing to review.',
  NOT_FOUND: 'This provider no longer exists. Refresh the page.',
};
// Exactly the four codes the two routes can raise -- `decideProviderVerification`
// throws PROVIDER_ALREADY_REVIEWED, NOT_A_PROVIDER or NOT_FOUND, and
// `decideProviderPhoto` throws NO_PHOTO_AWAITING_REVIEW, NOT_A_PROVIDER or
// NOT_FOUND. A key for a code the route cannot emit is a sentence no one will
// ever read, and it invites the next reader to believe the case is handled.

/**
 * Approve or reject one provider.
 *
 * Re-fetches the badge count on success rather than decrementing it locally:
 * two admins can be working the same queue, so the only number worth showing is
 * the one the server just recomputed.
 */
export const decideProviderVerification = createAsyncThunk(
  'providers/verification/patch',
  async (
    {
      id,
      decision,
      reason,
    }: { id: string; decision: 'approve' | 'reject'; reason?: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const res = await decideProviderVerificationRequest(id, decision, reason);

      dispatch(getPendingProvidersCount());

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, DECISION_ERRORS));
    }
  },
);

export const getPendingPhotos = createAsyncThunk(
  'providers/photos/pending/get',
  async (query: any = {}, { rejectWithValue }) => {
    try {
      const res = await getPendingPhotosRequest(query);

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

export const getPendingPhotosCount = createAsyncThunk(
  'providers/photos/pending/count',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getPendingPhotosCountRequest();

      return res.data?.count ?? 0;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

/**
 * Approve or reject one provider's photo.
 *
 * Both the count and the queue are re-fetched from the server rather than
 * adjusted locally: a provider can replace their photo while an admin has the
 * queue open, so a locally decremented number would drift from reality within
 * seconds of two people working it at once.
 */
export const decideProviderPhoto = createAsyncThunk(
  'providers/photo/patch',
  async (
    {
      id,
      decision,
      reason,
    }: { id: string; decision: 'approve' | 'reject'; reason?: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const res = await decideProviderPhotoRequest(id, decision, reason);

      dispatch(getPendingPhotosCount());

      return res.data;
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, DECISION_ERRORS));
    }
  },
);

export const doctorThunk = {
  getDoctors,
  getDoctor,
  editUser,
  getDoctorVisits,
  getDoctorFinances,
  getPendingProviders,
  getPendingProvidersCount,
  decideProviderVerification,
  getPendingPhotos,
  getPendingPhotosCount,
  decideProviderPhoto,
};
